import React, { ComponentProps, ReactElement, useContext, useMemo, useState } from 'react';
import { default as Form } from '../../components/connectedForm';
import * as entities from '../../graphql/entities';
import { Assembly as entity, AssemblyAttributeConfig, AssemblyNoteType, Product } from '../../graphql/entities';
import createForm from '../../components/connectedForm/ConnectedForm';
import { useDispatch } from 'react-redux';
import { useFormQuery } from '../../graphql/containers/useFormQuery';
import { useLazyQuery, useMutation } from '@apollo/client';
import { IDropdownProps } from '../../@types/dropdown';
import * as input from '../../components/connectedForm/inputComponents';
import {
  AssemblyStatus,
  Direction,
  FilterOperation,
  IAssembly,
  IAssemblyComponent,
  IAssemblyComponentInformation,
  IAssemblyComponentInformationQueryResult,
  IAssemblyComponentInformationQueryVariables,
  IAssemblyComponentPurchaseRequirementCriteriaInput,
  IAssemblyInput,
  IAssemblyNotesQueryResult,
  IAssemblyNotesQueryVariables,
  IAssemblyRequirementDetailForSubAssyCriteriaInput,
  IBinLocation,
  IDeleteAssemblyAssignmentsMutationResult,
  IDeleteAssemblyAssignmentsMutationVariables,
  IFilterInput,
  IGenerateAssemblyRequirementDetailForSubAssemblyMutationResult,
  IGenerateAssemblyRequirementDetailForSubAssemblyMutationVariables,
  IGenerateRequirementDetailForAssemblyRequirementMutationResult,
  IGenerateRequirementDetailForAssemblyRequirementMutationVariables,
  IProduct,
  IProductComponent,
  IProductQueryResult,
  IProductQueryVariables,
  IStockTotal,
  IUnitOfMeasureEmbeddable,
  ProductClass,
  TargetEntity,
  UnitOfMeasureFactorDirection,
} from '../../graphql/schema/p4types';
import { IDateFieldProps } from '../../components/connectedForm/DateInput/DateInput';
import ConnectedFormContext from '../../components/Context/ConnectedFormContext';
import WorkspaceContext, { WorkspaceContextType } from '../../components/Context/WorkspaceContext';
import { useComponentQueryErrorHandling } from '../../utils/parseErrorResponse';
import { BlurTextFieldType, IBlurTextFieldProps } from '../../components/connectedForm/BlurTextField/BlurTextField';
import fieldMetaData from '../../graphql/schema/fieldMetaData';
import { IAttributeArrayProps } from '../../components/connectedForm/AttributeArray';
import { IFieldOnChange } from '../../components/connectedForm/Field';
import { cloneDeep } from '@apollo/client/utilities';
import { ComponentGrid } from '../../components/ComponentGrid';
import { AssemblyFormAction, useAssemblyFormActions } from '../../actions/product/AssemblyFormAction';
import { EntityType, IFormAction } from '../../@types/entityTypes';
import { getEntryCount } from '../../utils/CountUtil';
import MemoEditor, { IMemoEditorType, useMemoEditorProps } from '../../components/connectedForm/MemoEditor/MemoEditor';
import AssemblyNotesWorkspaceQuery from '../../graphql/queries/product/AssemblyNotesWorkspaceQuery.graphql';
import AssemblyComponentInformationQuery from '../../graphql/queries/product/AssemblyComponentInformationQuery.graphql';
import InputBlock from '../../components/connectedForm/InputBlock';
import InputLabel from '../../components/connectedForm/InputLabel';
import InputGroup from '../../components/connectedForm/InputGroup';
import { useParams } from 'react-router';
import GridContainer from '../../components/connectedForm/GridContainer';
import AuditByFieldWorkspace from '../company/AuditByFieldWorkspace';
import DocumentSummaryWorkspace from '../company/DocumentSummaryWorkspace';
import { ISubmitOptionEntry } from '../../components/SelectButton';
import { uuIdGenerator } from '../../utils/UuIdGenerator';
import { GenerateRequirementDetailForAssemblyRequirement } from '../../graphql/mutations/product/GenerateRequirementDetailForAssemblyRequirement.graphql';
import { DeleteAssemblyAssignments } from '../../graphql/mutations/product/DeleteAssemblyAssignments.graphql';
import getClientMutationId from '../../utils/getClientMutationId';
import { useBackgroundMutation } from '../../utils/BackgroundJobs';
import { GenerateAssemblyRequirementDetailForSubAssembly } from '../../graphql/mutations/product/GenerateAssembluRequirementDetailForSubAssembly.graphql';
import { DynamicReportModal, useDynamicReportOptions } from '../../actions/useDynamicReportAction';
import { ExpansionUUIDProvider } from '../../components/Context/ExpansionUUIDProvider';
import normalizeVariables from '../../graphql/schema/normalizeVariables';
import { MenuItem } from 'react-contextmenu';
import GridToolbarWorkspaceSearch from '../../components/GridToolbar/GridToolbarWorkspaceSearch';
import GridToolbarWorkspaceFiltersContainer from '../../components/GridToolbar/GridToolbarWorkspaceFiltersContainer';
import GridToolbarWorkspaceRowCounter from '../../components/GridToolbar/GridToolbarWorkspaceRowCounter';
import GridToolbarContainer, { GridToolbarRightItems } from '../../components/GridToolbar/GridToolbarContainer';
import GridToolbarWorkspaceRefreshButton from '../../components/GridToolbar/GridToolbarWorkspaceRefreshButton';
import { DangerGridToolbarButton } from '../../components/GridToolbar/GridToolbarButton';
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
} from '@fluentui/react-components';
import FluentModal from '../../components/modals/FluentModal';
import EditableDataGrid from '../../components/EditableDataGrid/EditableDataGrid';
import { IEditableDataGridColumnDef } from '../../@types/editableDataGridTypes';
import Formatters from '../../components/grid-addons/formatters';
import DropdownEditorClean from '../../components/DropdownEditor/DropdownEditorClean';

const LazyAssemblyAssignmentWorkspace = React.lazy(() => import('../../views/product/AssemblyAssignmentWorkspace'));
const LazyGoodsReceiptLineWorkspace = React.lazy(() => import('../../views/product/GoodsReceiptLineWorkspace'));

const getAssemblyStatusIcon = (status: AssemblyStatus): { color: string; type: string } => {
  let icon = { color: 'default', type: 'info' };

  switch (status) {
    case AssemblyStatus.Draft:
      icon = {
        type: 'assignment',
        color: 'warning',
      };
      break;
    case AssemblyStatus.Unprocessed:
      icon = {
        type: 'error',
        color: 'danger',
      };
      break;
    case AssemblyStatus.InAssembly:
    case AssemblyStatus.PartComplete:
      icon = {
        type: 'access_time',
        color: 'warning',
      };
      break;
    case AssemblyStatus.Complete:
      icon = {
        type: 'check_circle',
        color: 'success',
      };
      break;
  }

  return icon;
};

const STATUS_FLAGS = [
  {
    key: 'status',
    status: fieldMetaData.AssemblyStatus.enumValues.map((entry) => {
      const icon = getAssemblyStatusIcon(entry.value!);
      return {
        value: entry.value,
        title: entry.label,
        style: icon.color,
        icon: icon.type,
      };
    }),
  },
];

const AssemblyAssignmentToolbar = ({
  assemblyId,
  deleteAssemblyAssignments,
}: {
  assemblyId: number;
  deleteAssemblyAssignments: (assignments: any[], onComplete: () => void) => void;
}) => {
  const { workspaceSelection, reloadData } = useContext(WorkspaceContext);
  const rowsSelected = workspaceSelection.selectedRows.length;
  const deleteAssignments = () => {
    deleteAssemblyAssignments(workspaceSelection.selectedRows, reloadData);
    workspaceSelection?.onRowSelect?.([], []);
  };

  return (
    <GridToolbarContainer>
      <GridToolbarWorkspaceSearch />
      <GridToolbarWorkspaceFiltersContainer />
      <GridToolbarWorkspaceRowCounter />
      {assemblyId !== null && assemblyId !== undefined ? (
        <GridToolbarRightItems>
          <Dialog modalType="alert">
            <DialogTrigger disableButtonEnhancement>
              <DangerGridToolbarButton disabled={!rowsSelected || rowsSelected === 0}>Delete</DangerGridToolbarButton>
            </DialogTrigger>
            <DialogSurface>
              <DialogBody>
                <DialogTitle>Are You Sure?</DialogTitle>
                <DialogContent>This action will delete the selected assignments and cannot be reversed.</DialogContent>
                <DialogActions>
                  <DialogTrigger disableButtonEnhancement>
                    <Button appearance="secondary">Close</Button>
                  </DialogTrigger>
                  <DialogTrigger disableButtonEnhancement>
                    <Button appearance="primary" onClick={deleteAssignments}>
                      Yes
                    </Button>
                  </DialogTrigger>
                </DialogActions>
              </DialogBody>
            </DialogSurface>
          </Dialog>
          <GridToolbarWorkspaceRefreshButton />
        </GridToolbarRightItems>
      ) : null}
    </GridToolbarContainer>
  );
};

const AssemblyForm = ({
  values,
  activeTabId,
  saveOptions,
  noDeleteOption,
}: {
  values: IAssembly;
  activeTabId?: string;
  saveOptions?: ISubmitOptionEntry[];
  noDeleteOption?: boolean;
}) => {
  const dispatch = useDispatch();
  const { change } = useContext(ConnectedFormContext);
  const handleQueryError = useComponentQueryErrorHandling();
  const [actions, modals] = useAssemblyFormActions();

  const [potentialUnitsOfMeasure, setPotentialUnitsOfMeasure] = useState<Array<IUnitOfMeasureEmbeddable>>([]);

  const [fetchProduct] = useLazyQuery<IProductQueryResult, IProductQueryVariables>(Product.instance!, {
    onError: handleQueryError,
    fetchPolicy: 'network-only',
  });

  const [fetchAssemblyComponentInformation] = useLazyQuery<
    IAssemblyComponentInformationQueryResult,
    IAssemblyComponentInformationQueryVariables
  >(AssemblyComponentInformationQuery, {
    fetchPolicy: 'network-only',
  });

  const [deleteAssemblyAssignments] = useBackgroundMutation<
    IDeleteAssemblyAssignmentsMutationResult,
    IDeleteAssemblyAssignmentsMutationVariables
  >(DeleteAssemblyAssignments, {
    fetchPolicy: 'network-only',
    onCompleteJob: () => {},
  });

  const [generateComponentShortage] = useBackgroundMutation<
    IGenerateRequirementDetailForAssemblyRequirementMutationResult,
    IGenerateRequirementDetailForAssemblyRequirementMutationVariables
  >(GenerateRequirementDetailForAssemblyRequirement, {
    fetchPolicy: 'network-only',
    onCompleteJob: () => {},
  });

  const [generateSubAssemblyShortage] = useBackgroundMutation<
    IGenerateAssemblyRequirementDetailForSubAssemblyMutationResult,
    IGenerateAssemblyRequirementDetailForSubAssemblyMutationVariables
  >(GenerateAssemblyRequirementDetailForSubAssembly, {
    fetchPolicy: 'network-only',
    onCompleteJob: () => {},
  });

  const dropdownOptions = Product;

  const componentReturnColumns: Array<IEditableDataGridColumnDef> = [
    {
      name: 'Product',
      key: 'product',
      editable: true,
      editor: <DropdownEditorClean />,
      formatter: 'dropdown',
      width: 250,
      resizable: false,
      options: dropdownOptions,
    },
    {
      name: 'Description',
      key: 'product.description',
      editable: false,
      resizable: true,
    },
    {
      name: 'Product Class',
      key: 'product.productClass',
      editable: false,
      resizable: true,
    },
    {
      name: 'Levelled',
      key: 'product.leveled',
      formatter: Formatters.BooleanFormatter,
      editable: false,
      resizable: true,
    },
    {
      name: 'Quantity',
      key: 'quantity',
      formatter: 'numeric',
      editable: true,
      resizable: true,
      options: {
        format: {
          decimals: (rowData: IAssemblyComponent) => {
            return rowData?.assemblyComponent?.productComponent?.component?.precision || 0;
          },
        },
      },
    },
  ];

  const dynamicReportView: any = {
    columns: [
      {
        name: 'Name',
        fieldName: 'component.reference',
        title: 'Name',
      },
      {
        name: 'Description',
        fieldName: 'component.description',
        title: 'Description',
      },
      {
        name: 'Product Class',
        fieldName: 'component.productClass',
        title: 'Product Class',
      },
      {
        name: 'Bin Location',
        fieldName: 'defaultBinLocation.location',
        title: 'Default Bin Loc',
      },
      {
        name: 'Leveled',
        fieldName: 'component.leveled',
        title: 'Leveled',
      },
      {
        name: 'Quantity',
        fieldName: 'quantity',
        title: 'Quantity',
      },
      {
        name: 'Assigned',
        fieldName: 'assigned',
        title: 'Assigned',
      },
      {
        name: 'Reserved',
        fieldName: 'reserved',
        title: 'Reserved',
      },
      {
        name: 'Received',
        fieldName: 'received',
        title: 'Received',
      },
      {
        name: 'Required Qty',
        fieldName: 'requiredQuantity',
        title: 'Required Quantity',
      },
      {
        name: 'Stock Level',
        fieldName: 'stockLevel',
        title: 'Stock Level',
      },
      {
        name: 'Free Stock',
        fieldName: 'freeStock',
        title: 'Free Stock',
      },
      {
        name: 'On Order',
        fieldName: 'stockTotal.onOrder',
        title: 'On Order',
      },
    ],
    filters: [
      {
        filterOperation: FilterOperation.Equals,
        filterParameters: {
          field: 'assemblyId',
          value: values.id,
        },
      },
    ],
  };

  const [printAction, dynamicReportModal] = useAssemblyComponentInformationDynamicReportAction(
    dynamicReportView,
    values.id
  );

  actions.push(printAction);

  const isReadOnly = useMemo(() => {
    return values.id && values.status !== 'DRAFT';
  }, [values]);

  const assemblyAuditPredefinedFilters = useMemo<Array<IFilterInput>>(() => {
    return [
      {
        filterOperation: FilterOperation.Equals,
        filterParameters: {
          field: 'id',
          value: values.id,
        },
      },
    ];
  }, [values.id]);

  const onProductChange: IFieldOnChange = async (_, newField) => {
    if (!newField?.id) {
      return;
    }

    const { data } = await fetchProduct({
      variables: {
        id: newField.id,
      },
    });

    // copy attribute values from product and reform
    let assemblyAttributeValues = cloneDeep(data?.data?.assemblyAttributeValues);
    assemblyAttributeValues = assemblyAttributeValues?.map((assemblyAttributeValue) => {
      delete assemblyAttributeValue?.uuid;
      return {
        ...assemblyAttributeValue,
        id: null,
        uuid: uuIdGenerator(),
        concurrencyVersion: null,
      };
    });

    let componentReturns = cloneDeep(data?.data?.componentReturns);
    componentReturns = componentReturns?.map((componentReturn) => {
      delete componentReturn?.uuid;
      return {
        ...componentReturn,
        id: null,
        uuid: uuIdGenerator(),
        concurrencyVersion: null,
      };
    });

    // copy alternate unit of measure from product
    let alternateUnitQuantities = [
      data?.data?.alternateSellingUnit1 as IUnitOfMeasureEmbeddable,
      data?.data?.alternateSellingUnit2 as IUnitOfMeasureEmbeddable,
      data?.data?.alternateSellingUnit3 as IUnitOfMeasureEmbeddable,
      data?.data?.alternateSellingUnit4 as IUnitOfMeasureEmbeddable,
      data?.data?.alternateSellingUnit5 as IUnitOfMeasureEmbeddable,
      data?.data?.alternateSellingUnit6 as IUnitOfMeasureEmbeddable,
      data?.data?.alternateSellingUnit7 as IUnitOfMeasureEmbeddable,
      data?.data?.alternateSellingUnit8 as IUnitOfMeasureEmbeddable,
      data?.data?.alternateSellingUnit9 as IUnitOfMeasureEmbeddable,
      data?.data?.alternateSellingUnit10 as IUnitOfMeasureEmbeddable,
    ].filter((uom) => uom?.description);

    //if there are some unit of measure quantities, add a default value
    if (alternateUnitQuantities.length > 0) {
      const defaultUnitOfMeasureEmbeddable = {
        description: data?.data?.sellingUnitDescription ?? 'Each',
        quantityFactor: 1,
        factorDirection: UnitOfMeasureFactorDirection.FromBaseUnit,
      };

      alternateUnitQuantities = [defaultUnitOfMeasureEmbeddable, ...alternateUnitQuantities];
    }

    dispatch(change('assemblyAttributeValues', assemblyAttributeValues));
    dispatch(change('componentReturns', componentReturns));
    setPotentialUnitsOfMeasure(alternateUnitQuantities);
  };

  const attachmentsCount = getEntryCount(values.attachments);

  const memoEditorProps = useMemoEditorProps<IAssemblyNotesQueryResult, IAssemblyNotesQueryVariables>(
    AssemblyNotesWorkspaceQuery,
    IMemoEditorType.Assembly,
    {
      predefinedFilters: [
        {
          filterOperation: FilterOperation.Equals,
          filterParameters: {
            field: 'assembly.id',
            value: values.id,
          },
        },
      ],
    }
  );

  interface IComponentInformation {
    id: number;
    component: IProduct;
    quantity: number;
    stockLevel: number;
    totalRequired: number;
    freeStock: number;
    reserved: number;
    topLevel: boolean;
    assigned: number;
    stockTotal: IStockTotal;
    defaultBinLocation: IBinLocation;
  }

  const createComponentShortage = (componentInfo: { productComponent: IComponentInformation }, fullQty: boolean) => {
    const criteria: IAssemblyComponentPurchaseRequirementCriteriaInput = {
      assembly: { id: values.id } as IAssemblyInput,
      productComponent: { id: componentInfo?.productComponent.id },
      amount: fullQty
        ? componentInfo?.productComponent.totalRequired
        : componentInfo?.productComponent.totalRequired -
          componentInfo?.productComponent.freeStock -
          componentInfo?.productComponent.assigned -
          componentInfo?.productComponent.reserved,
    };

    generateComponentShortage({
      variables: {
        input: {
          clientMutationId: getClientMutationId(),
          purchaseRequirementCriteria: criteria,
        },
      },
    });
  };

  const createSubAssemblyShortage = (componentInfo: { productComponent: IComponentInformation }, fullQty: boolean) => {
    const criteria: IAssemblyRequirementDetailForSubAssyCriteriaInput = {
      parentAssembly: { id: values.id },
      productComponent: { id: componentInfo?.productComponent.id },
      fullAmount: fullQty,
    };

    generateSubAssemblyShortage({
      variables: {
        input: {
          clientMutationId: getClientMutationId(),
          creationCriteria: criteria,
        },
      },
    });
  };

  //add actions here... might need moving up a level so that these options are not provided for reservations
  const renderComponentLineActionButtons = ({ rowData }: { rowData: { productComponent: IComponentInformation } }) => {
    const renderedButtons: Array<ReactElement> = [];
    const availability =
      (rowData?.productComponent?.freeStock ?? 0) +
      (rowData?.productComponent?.assigned ?? 0) +
      (rowData?.productComponent?.reserved ?? 0);
    const alreadyAllocated = (rowData?.productComponent?.assigned ?? 0) + (rowData?.productComponent?.reserved ?? 0);

    if (
      !values.id ||
      !rowData?.productComponent?.topLevel ||
      rowData?.productComponent?.totalRequired <= availability
    ) {
      return renderedButtons;
    }

    if (
      rowData &&
      rowData?.productComponent?.component?.productClass === ProductClass.Standard &&
      rowData?.productComponent?.component?.leveled
    ) {
      renderedButtons.push(
        <MenuItem key={'addPurchaseShortageShort'} onClick={() => createComponentShortage(rowData, false)}>
          {'Add Purchase Shortage (Short Amount)'}
        </MenuItem>
      );
      if (alreadyAllocated === 0) {
        renderedButtons.push(
          <MenuItem key={'addPurchaseShortageFull'} onClick={() => createComponentShortage(rowData, true)}>
            {'Add Purchase Shortage (Full Amount)'}
          </MenuItem>
        );
      }
    }

    if (
      rowData?.productComponent?.component?.productClass === ProductClass.Assembly &&
      rowData?.productComponent?.component?.leveled
    ) {
      renderedButtons.push(
        <MenuItem key={'addAssemblyShortageFull'} onClick={() => createSubAssemblyShortage(rowData, true)}>
          {'Add Assembly Shortage (Full Amount)'}
        </MenuItem>
      );
    }

    if (alreadyAllocated === 0) {
      if (
        rowData.productComponent?.component?.productClass === ProductClass.Assembly &&
        rowData.productComponent?.component?.leveled
      ) {
        renderedButtons.push(
          <MenuItem key={'addAssemblyShortageShort'} onClick={() => createSubAssemblyShortage(rowData, false)}>
            {'Add Assembly Shortage (Short Amount)'}
          </MenuItem>
        );
      }
    }

    return renderedButtons;
  };

  const extraColumms = [
    {
      name: 'Total Requirement',
      key: 'productComponent.totalRequired',
      formatter: 'numeric',
      editable: false,
      options: {
        format: {
          decimals: (rowData: IAssemblyComponent) => {
            return rowData?.productComponent?.component?.precision || 0;
          },
        },
      },
    },
    {
      name: 'Assigned',
      key: 'productComponent.assigned',
      formatter: 'numeric',
      editable: false,
      options: {
        format: {
          decimals: (rowData: IAssemblyComponent) => {
            return rowData?.productComponent?.component?.precision || 0;
          },
        },
      },
    },
    {
      name: 'Reserved',
      key: 'productComponent.reserved',
      formatter: 'numeric',
      editable: false,
      options: {
        format: {
          decimals: (rowData: IAssemblyComponent) => {
            return rowData?.productComponent?.component?.precision || 0;
          },
        },
      },
    },
    {
      name: 'Free Stock',
      key: 'productComponent.freeStock',
      formatter: 'numeric',
      editable: false,
      resizable: true,
      options: {
        format: {
          decimals: (rowData: IAssemblyComponent) => {
            return rowData?.productComponent?.component?.precision || 0;
          },
        },
      },
    },
    {
      name: 'Stock Level',
      key: 'productComponent.stockLevel',
      formatter: 'numeric',
      editable: false,
      resizable: true,
      options: {
        format: {
          decimals: (rowData: IAssemblyComponent) => {
            return rowData?.productComponent?.component?.precision || 0;
          },
        },
      },
    },
    {
      name: 'On Order',
      key: 'productComponent.stockTotal.onOrder',
      formatter: 'numeric',
      editable: false,
      resizable: true,
      options: {
        format: {
          decimals: (rowData: IProductComponent) => {
            return rowData?.component?.precision || 0;
          },
        },
      },
    },
    {
      name: 'Default Bin Location',
      key: 'productComponent.defaultBinLocation.location',
      editable: false,
      resizable: true,
    },
  ];

  const deleteAssemblyAssignmentsAction = (assignments: any[], onComplete: () => void) => {
    deleteAssemblyAssignments({
      variables: {
        input: {
          clientMutationId: getClientMutationId(),
          assemblyAssignments: normalizeVariables(assignments),
        },
      },
      onCompleted: onComplete,
    });
  };

  const showUnits: boolean = (values?.id && values?.units) || potentialUnitsOfMeasure.length > 0;
  const complete = values?.status === AssemblyStatus.Complete;
  return (
    <>
      {modals}
      {dynamicReportModal}
      <Form.FormContainer>
        <Form.Header
          entityHdr="orderNumber"
          component={Form.HeaderBlock}
          statusFlags={STATUS_FLAGS}
          actionButtons={actions}
          saveOptions={saveOptions}
          noDeleteOption={noDeleteOption}
          archive
        />
        <Form.Form>
          <Form.Row>
            <Form.Fieldset cols={1 / 2}>
              <Form.Field<IDropdownProps>
                dataid="product"
                component={input.Dropdown}
                predefinedFilters={[
                  {
                    filterOperation: FilterOperation.Equals,
                    filterParameters: {
                      field: `productClass`,
                      value: ProductClass.Assembly,
                    },
                  },
                ]}
                label="Assembly Product"
                readOnly={isReadOnly}
                onChange={onProductChange}
                rawLabel
              />
              {values?.product ? (
                <InputBlock>
                  <InputLabel />
                  <InputGroup>{values?.product?.description}</InputGroup>
                </InputBlock>
              ) : null}
              <Form.Field<IDropdownProps>
                dataid="warehouse"
                component={input.Dropdown}
                label="Warehouse"
                readOnly={isReadOnly}
              />
              {showUnits ? (
                <Form.Field<IDropdownProps>
                  dataid="units"
                  component={input.Dropdown}
                  label="Units"
                  readOnly={values?.id}
                  options={potentialUnitsOfMeasure}
                  labelKey="description"
                />
              ) : null}
              <Form.Field<IBlurTextFieldProps>
                dataid="quantity"
                type={BlurTextFieldType.Number}
                component={input.BlurTextField}
                label="Quantity"
                readOnly={isReadOnly}
                decimals={values?.product?.precision || 0}
              />
              <Form.Field<IBlurTextFieldProps>
                dataid="received"
                type={BlurTextFieldType.Number}
                component={input.BlurTextField}
                label="Received"
                decimals={values?.product?.precision || 0}
                readOnly
              />
            </Form.Fieldset>
            <Form.Fieldset cols={1 / 2}>
              <Form.Field<IDateFieldProps>
                dataid="orderDate"
                component={input.DateField}
                label="Order Date"
                readOnly={complete}
              />
              {values.completedDate ? (
                <Form.Field<IDateFieldProps>
                  dataid="completedDate"
                  component={input.DateField}
                  label="Completion Date"
                  readOnly={true}
                />
              ) : null}
              <Form.Field<IDateFieldProps>
                dataid="expectedCompletionDate"
                component={input.DateField}
                label="Expected Completion Date"
                readOnly={complete}
              />
              <Form.Field<IDateFieldProps>
                dataid="nextProductionDate"
                component={input.DateField}
                label="Next Production Date"
                readOnly={complete}
              />
            </Form.Fieldset>
          </Form.Row>
          <Form.TabPanel active={activeTabId}>
            <Form.TabContent id="components" label="Components">
              <ExpansionUUIDProvider>
                <ComponentGrid
                  parent={values}
                  extraColumns={extraColumms}
                  onProductSelected={
                    // if no ID then allow component grid to fetch components based on the selected product
                    // otherwise we need to fetch them from the assembly
                    values?.id
                      ? (productId, path, dataId) => {
                          fetchAssemblyComponentInformation({
                            variables: {
                              assemblyId: values.id,
                              path,
                              parentComponentProductId: productId,
                            },
                          }).then((response) => {
                            const assemblyComponentInformation = response?.data?.data;
                            dispatch(
                              change(
                                dataId,
                                (assemblyComponentInformation as Array<IAssemblyComponentInformation>)?.map<{
                                  id?: number;
                                  uuid?: string;
                                  productComponent: IComponentInformation;
                                }>((asc: IAssemblyComponentInformation) => {
                                  return {
                                    id: asc?.id,
                                    uuid: asc?.uuid,
                                    productComponent: {
                                      id: asc?.productComponentId,
                                      component: asc?.component,
                                      quantity: asc?.quantity,
                                      stockLevel: asc?.stockLevel,
                                      totalRequired: asc?.requiredQuantity,
                                      freeStock: asc?.freeStock,
                                      reserved: asc?.reserved,
                                      topLevel: asc?.topLevel,
                                      assigned: asc?.assigned,
                                      stockTotal: asc?.stockTotal,
                                      defaultBinLocation: asc?.defaultBinLocation,
                                    },
                                  };
                                })
                              )
                            );
                          });
                        }
                      : undefined
                  }
                  renderActionButtons={renderComponentLineActionButtons}
                  readonly={values?.status !== AssemblyStatus.Unprocessed}
                  onChange={(path, value) => dispatch(change(path, value))}
                  deleteEnabled={values?.id && values?.status !== AssemblyStatus.Complete}
                  extraRowEnabled={values?.id}
                />
              </ExpansionUUIDProvider>
            </Form.TabContent>
            <Form.TabContent id="componentReturns" label="Component Returns">
              <Form.Field
                dataid="componentReturns"
                component={EditableDataGrid}
                enableCellSelect={true}
                columns={componentReturnColumns}
                addBlankRow={true}
                deleteOption={(rowIdx) => {
                  dispatch(change(`componentReturns.${rowIdx}.deleted`, true));
                }}
              />
            </Form.TabContent>
            <Form.TabContent id="attributes" label="Attributes" shouldRender={!!values?.product}>
              <Form.Row>
                <Form.Fieldset cols={2 / 3}>
                  <Form.Field<IAttributeArrayProps>
                    dataid="assemblyAttributeValues"
                    component={input.AttributeArray}
                    label="Attributes"
                    query={AssemblyAttributeConfig.list}
                    initialized={true}
                    type="assembly"
                  />
                </Form.Fieldset>
              </Form.Row>
            </Form.TabContent>
            <Form.TabContent id="assignments" label="Assignments" shouldRender={!!values?.id} onlyRenderWhenActive>
              <LazyAssemblyAssignmentWorkspace
                viewScreenName="assemblyAssignments"
                embeddedView
                allowView={false}
                disableViewToolbar={true}
                predefinedFilters={[
                  {
                    filterOperation: FilterOperation.Equals,
                    filterParameters: {
                      field: 'assembly.id',
                      value: values?.id,
                    },
                  },
                ]}
              >
                <AssemblyAssignmentToolbar
                  assemblyId={values?.id}
                  deleteAssemblyAssignments={deleteAssemblyAssignmentsAction}
                />
              </LazyAssemblyAssignmentWorkspace>
            </Form.TabContent>
            <Form.TabContent id="receipts" label="Receipts" shouldRender={!!values?.id} onlyRenderWhenActive>
              <LazyGoodsReceiptLineWorkspace
                viewScreenName="goodsReceiptLines"
                embeddedView
                allowView={false}
                predefinedFilters={[
                  {
                    filterOperation: FilterOperation.Equals,
                    filterParameters: {
                      field: 'goodsReceipt.assembly.id',
                      value: values.id,
                    },
                  },
                ]}
                defaultColumns={['goodsReceipt.receiptDate', 'binLocation.location', 'costPrice', 'quantity']}
                initialSortColumn="goodsReceipt.receiptDate"
                initialSortDir={Direction.Desc}
              />
            </Form.TabContent>
            <Form.TabContent
              id="attachments"
              label="Attachments"
              badge={attachmentsCount}
              onlyRenderWhenActive={true}
              shouldRender={!!values?.id}
            >
              <Form.Row>
                <Form.Fieldset cols={1}>
                  <Form.Legend label="Attachments" />
                  <Form.Field dataid="attachments" component={input.GridUploader} />
                </Form.Fieldset>
              </Form.Row>
            </Form.TabContent>
            <Form.TabContent
              id="notesTab"
              label="Notes"
              badge={memoEditorProps.queryProps.data?.listData?.count}
              type="info"
              onlyRenderWhenActive={true}
              shouldRender={!!values?.id}
            >
              <MemoEditor noteTypeEntity={AssemblyNoteType} {...memoEditorProps} />
            </Form.TabContent>
            <Form.TabContent
              id="documents"
              label="Document History"
              tabStyle={{ width: '140px' }}
              shouldRender={!!values?.id}
              onlyRenderWhenActive={true}
            >
              <DocumentSummaryWorkspace
                predefinedFilters={[
                  {
                    filterOperation: FilterOperation.Equals,
                    filterParameters: {
                      field: 'transactionId',
                      value: values?.id,
                    },
                  },
                  {
                    filterOperation: FilterOperation.Equals,
                    filterParameters: {
                      field: 'transactionEntity',
                      value: TargetEntity.AssemblyOrder,
                    },
                  },
                ]}
                viewScreenName="/Common/DocumentSummary/Modal"
                preview
                embeddedView
                removeAddButton
              />
            </Form.TabContent>
            <Form.TabContent
              id="audit"
              label="Audit"
              key="audit"
              shouldRender={!!values?.id}
              onlyRenderWhenActive={true}
            >
              <GridContainer>
                <AuditByFieldWorkspace
                  embeddedView={true}
                  predefinedFilters={assemblyAuditPredefinedFilters}
                  viewScreenName="/Products/Assembly/Assembly/AssemblyAudit/Workspace"
                  entity={entities.AssemblyAuditByField}
                />
              </GridContainer>
            </Form.TabContent>
          </Form.TabPanel>
        </Form.Form>
      </Form.FormContainer>
    </>
  );
};

const getSubmitValues = ({ values }: { values: IAssembly }) => {
  // a deep clone
  const assembly = cloneDeep(values);

  //remove the info. fields from the components so that they
  //conform to IProductComponentInput
  assembly.components = assembly?.id
    ? assembly?.components?.map((component) => {
        component!.productComponent = normalizeVariables(component?.productComponent, [
          'stockLevel',
          'totalRequired',
          'freeStock',
          'reserved',
          'topLevel',
          'assigned',
          'defaultBinLocation',
          'stockTotal',
          'balanceOnly',
        ]);
        return component;
      })
    : [];

  //if the units on the values is not set to 'None' do nothing
  if (values.units?.description !== 'None') {
    return assembly;
  }

  if (assembly.units?.description === 'None') {
    assembly.units = null;
  }

  return assembly;
};

const ConnectedAssemblyForm = createForm<ComponentProps<typeof AssemblyForm>, IAssembly>({
  form: entity.type,
  name: entity.formTitle,
  entity: entity,
  queryFragment: entity.fragment,
  defaultReturnUrl: entity.workspaceUrl,
  getSubmitValues: getSubmitValues,
})(AssemblyForm);

const useAssemblyComponentInformationDynamicReportAction = (
  currentView: any,
  assemblyId: number
): [IFormAction<AssemblyFormAction>, JSX.Element] => {
  const [dynamicReportModalOpen, setDynamicReportModalOpen] = useState(false);

  const { options, tooltip } = useDynamicReportOptions();
  const dynamicReportAction = {
    key: 'dynamicReport',
    text: 'Dynamic Report',
    onClick: () => {
      setDynamicReportModalOpen(true);
    },
    actionReference: AssemblyFormAction.PrintDynamicReport,
    condition: () => true,
    disabled: options?.length === 0,
    tooltip: tooltip,
  };

  return [
    dynamicReportAction,
    <FluentModal
      isOpen={dynamicReportModalOpen}
      styles={{ main: { minWidth: '500px', paddingBottom: '60px' } }}
      key={assemblyId}
    >
      <WorkspaceContext.Provider
        value={
          {
            workspaceFilters: [
              {
                filterOperation: FilterOperation.Equals,
                filterParameters: {
                  field: 'assemblyId',
                  value: assemblyId,
                },
              },
            ],
            workspaceSelection: { selectedAll: false },
          } as unknown as WorkspaceContextType
        }
      >
        <DynamicReportModal
          title={'Print Assembly Component Information'}
          view={currentView}
          entityType={'AssemblyComponentInformation'}
          canonicalName={'p4.function.product.assembly.AssemblyComponentInformation'}
          onClose={() => setDynamicReportModalOpen(false)}
          options={options}
        />
      </WorkspaceContext.Provider>
    </FluentModal>,
  ];
};

export default (props: ComponentProps<typeof ConnectedAssemblyForm>) => {
  const [formUpdate] = useMutation(entity.update!, { refetchQueries: [AssemblyNotesWorkspaceQuery] });
  const params: { productid: string } = useParams();

  const { formData } = props;

  const productId = params?.productid;
  const defaultVariables = { assemblyValues: { productId } };
  let formDefault;

  if (formData) {
    formDefault = null;
  } else {
    formDefault = useFormQuery({ default: entity.default, instance: entity.instance } as EntityType, {
      defaultVariables,
    });
  }

  return <ConnectedAssemblyForm {...props} formUpdate={formUpdate} formData={formData || formDefault} />;
};
